/*
 * Два хоста у одной и той же ссылки:
 * — короткий, `qx.or`, показывается внутри мессенджера и веб-клиента и попадает в текст сообщения;
 * — публичный, `link.qunixor.org`, по которому ссылка реально открывается в браузере: он отдаёт
 *   превью-карточку пира и уводит либо в приложение, либо в веб-клиент.
 *
 * Правило выбора одно: всё, что остаётся внутри наших клиентов (текст пузыря, поле ссылки, label),
 * — короткий хост; всё, что уходит наружу (буфер обмена, «поделиться», QR, window.open), — публичный.
 * Разбор ссылок принимает оба, поэтому ссылка, вставленная в переписку любым хостом, открывается.
 */

export const SHORT_LINK_HOST = 'qx.or';
export const PUBLIC_LINK_HOST = 'link.qunixor.org';

// Схема запуска нативного приложения: её печатает превью-страница в кнопке и её же можно
// встретить в переписке. Для веб-клиента это тот же `tg://`, только с нашим именем.
export const APP_LINK_SCHEME = 'qunixor';

// Порядок важен: разбиралка сравнивает хост целиком, а ссылки наружу строятся с публичного.
export const LINK_HOSTS = [PUBLIC_LINK_HOST, SHORT_LINK_HOST];

// Хосты, по которым ходили ссылки до наших: в базе и в чужих сообщениях они ещё встречаются.
// Путь от них тот же, поэтому показываем короткий хост, а не чужой бренд.
export const LEGACY_LINK_HOSTS = ['t.me', 'telegram.me'];

const escapeHost = (host: string) => host.replace(/\./g, '\\.');

const KNOWN_HOSTS_REG_EXP = new RegExp(
  `^(?:https?:\\/\\/)?(?:${[...LINK_HOSTS, ...LEGACY_LINK_HOSTS].map(escapeHost).join('|')})\\/`,
  'i'
);

/** Ссылка → путь без хоста и схемы. Чужая ссылка возвращается как есть. */
export function linkPath(url: string) {
  const match = url.match(KNOWN_HOSTS_REG_EXP);
  return match ? url.slice(match[0].length) : url;
}

// Любой известный хост, включая чужой: в переписке остаётся путь, а хост показываем свой.
// Сворачиваем и `t.me`, потому что ссылка на чужой бренд внутри нашего мессенджера — это всё ещё
// ссылка на нас: разбор пути от неё тот же.
const ANY_HOST_REG_EXP = new RegExp(
  `(^|://)(?:${[...LINK_HOSTS, ...LEGACY_LINK_HOSTS].map(escapeHost).join('|')})(?=[/?#]|$)`,
  'i'
);

/** Ссылка для показа внутри приложения: публичный и старый хосты сворачиваются в короткий. */
export function displayLink(url: string) {
  return url.replace(ANY_HOST_REG_EXP, `$1${SHORT_LINK_HOST}`);
}

/** Путь вида `Lytkin`, `+hash`, `boost/Lytkin` → короткая ссылка для переписки. */
export function shortLink(path: string) {
  return `https://${SHORT_LINK_HOST}/${linkPath(path).replace(/^\/+/, '')}`;
}

/** Тот же путь, но ссылкой, которая работает в браузере: копировать, делиться, QR. */
export function shareableLink(pathOrUrl: string) {
  const path = linkPath(pathOrUrl);
  // Чужая абсолютная ссылка остаётся чужой: подставлять под неё свой хост — значит открывать
  // несуществующую страницу у нас.
  if(path === pathOrUrl && path.startsWith('http')) return path;
  return `https://${PUBLIC_LINK_HOST}/${path.replace(/^\/+/, '')}`;
}

// Сайт продукта: на него ведут справочные ссылки интерфейса («узнать больше», условия), которые
// в оригинальном клиенте указывают на telegram.org.
export const SITE_HOST = 'qunixor.org';

// Витрина NFT-юзернеймов и анонимных номеров: то, чем в оригинале занимается fragment.com.
export const MARKET_HOST = 'market.qunixor.org';

/** Ссылка на страницу витрины: `marketLink('username/Lytkin')`, `marketLink('numbers')`. */
export function marketLink(path: string) {
  return `https://${MARKET_HOST}/${linkPath(path).replace(/^\/+/, '')}`;
}

/** Ссылка на сайт, а не на чужую документацию. */
export function siteLink(path = '') {
  return `https://${SITE_HOST}/${linkPath(path).replace(/^\/+/, '')}`;
}

/** Подпись в интерфейсе: короткий хост без схемы, как в строке «Публичная ссылка». */
export function linkLabel(pathOrUrl: string) {
  const path = linkPath(pathOrUrl);
  return path.startsWith('http') ? path.replace(/^https?:\/\//i, '') : `${SHORT_LINK_HOST}/${path}`;
}
